import { z } from 'zod';
import { competitionCodeSchema, competitionTierSchema, qualityScoreSchema } from './international-competition.js';
const nonEmptyStringSchema = z.string().trim().min(1);
const timestampSchema = z.string().trim().min(1);
const nonNegativeIntSchema = z.number().int().min(0);
const nullableNonNegativeIntSchema = nonNegativeIntSchema.nullable();
const competitionCodesQuerySchema = z.preprocess((value) => {
    if (value === undefined || value === null || value === '')
        return undefined;
    const values = Array.isArray(value) ? value : [value];
    return values
        .flatMap(item => String(item).split(','))
        .map(item => item.trim())
        .filter(Boolean);
}, z.array(competitionCodeSchema).min(1).optional());
export const fieldConfidenceSchema = z.enum([
    'trusted',
    'definition_sensitive',
    'provider_specific',
    'single_source',
    'unverified',
    'rejected',
]);
export const importBatchStatusSchema = z.enum([
    'draft',
    'dry_run',
    'accepted',
    'superseded',
    'failed',
]);
export const researchProviderSchema = z.enum(['statsbomb', 'api_football']);
export const researchSeasons = [1990, 1994, 1998, 2002, 2006, 2010, 2014, 2018, 2022];
export const researchSeasonSchema = z.union([
    z.literal(1990),
    z.literal(1994),
    z.literal(1998),
    z.literal(2002),
    z.literal(2006),
    z.literal(2010),
    z.literal(2014),
    z.literal(2018),
    z.literal(2022),
]);
export const importBatchScopeSeasonSchema = z.union([
    researchSeasonSchema,
    z.number().int().min(2015).max(2030),
]);
export const evidenceSourceReferenceSchema = z.object({
    importBatchId: nonEmptyStringSchema,
    provider: researchProviderSchema,
    payloadHash: nonEmptyStringSchema.optional(),
});
export const fieldConfidenceMapSchema = z.record(nonEmptyStringSchema, fieldConfidenceSchema);
export const fieldConfidenceByProviderSchema = z.record(nonEmptyStringSchema, fieldConfidenceMapSchema);
export const adminSafeImportIssueRowSchema = z.object({
    provider: nonEmptyStringSchema.optional(),
    season: nonNegativeIntSchema.optional(),
    team: nonEmptyStringSchema.optional(),
    opponent: nonEmptyStringSchema.optional(),
    matchId: nonEmptyStringSchema.optional(),
    providerMatchId: nonEmptyStringSchema.optional(),
    field: nonEmptyStringSchema.optional(),
    status: nonEmptyStringSchema.optional(),
    reason: nonEmptyStringSchema.optional(),
    row: z.union([nonNegativeIntSchema, nonEmptyStringSchema]).optional(),
});
export const adminSafeProviderAuditSummarySchema = z.object({
    totalComparisons: nonNegativeIntSchema,
    byStatus: z.record(nonEmptyStringSchema, nonNegativeIntSchema),
    byField: z.record(nonEmptyStringSchema, nonNegativeIntSchema),
});
export const researchMatchSchema = z.object({
    id: nonEmptyStringSchema,
    importBatchId: nonEmptyStringSchema,
    season: importBatchScopeSeasonSchema,
    competition: nonEmptyStringSchema,
    stage: nonEmptyStringSchema,
    groupRound: nonEmptyStringSchema.nullable().default(null),
    kickoffAt: timestampSchema.nullable().default(null),
    homeTeam: nonEmptyStringSchema,
    awayTeam: nonEmptyStringSchema,
});
export const teamMatchStatSchema = z.object({
    id: nonEmptyStringSchema,
    importBatchId: nonEmptyStringSchema,
    matchId: nonEmptyStringSchema,
    season: importBatchScopeSeasonSchema,
    team: nonEmptyStringSchema,
    opponent: nonEmptyStringSchema,
    goals: nonNegativeIntSchema,
    shotsTotal: nullableNonNegativeIntSchema,
    shotsOnTarget: nullableNonNegativeIntSchema,
    corners: nullableNonNegativeIntSchema,
    expectedGoals: z.number().min(0).nullable(),
    fieldConfidence: fieldConfidenceMapSchema,
    fieldConfidenceByProvider: fieldConfidenceByProviderSchema,
    sourceReferences: z.array(evidenceSourceReferenceSchema),
});
export const importBatchSchema = z.object({
    id: nonEmptyStringSchema,
    status: importBatchStatusSchema,
    scope: z.object({
        seasons: z.array(importBatchScopeSeasonSchema),
        providers: z.array(researchProviderSchema),
        competitions: z.array(competitionCodeSchema).optional(),
    }),
    createdAt: timestampSchema,
    acceptedAt: timestampSchema.nullable().default(null),
    supersededAt: timestampSchema.nullable().default(null),
});
const limitSchema = z.coerce.number().int().min(1).max(500).default(50);
const offsetSchema = z.coerce.number().int().min(0).default(0);
const queryYearSchema = z.coerce.number().int().min(1900).max(2030).optional();
const booleanQuerySchema = z.union([
    z.boolean(),
    z.literal('true').transform(() => true),
    z.literal('false').transform(() => false),
]);
export const worldcupResearchSortFieldSchema = z.enum([
    'season',
    'team',
    'opponent',
    'goals',
    'shotsOnTarget',
    'corners',
    'shotsTotal',
    'expectedGoals',
]);
export const worldcupResearchMatchShapeSchema = z.enum([
    'shots_on_target_advantage_without_win',
    'corner_advantage',
    'high_xg_low_goals',
    'low_total_shots_high_scoring',
]);
export const sortDirectionSchema = z.enum(['asc', 'desc']);
export const worldcupResearchViewSchema = z.enum(['major_tournament', 'competitive_context', 'exploratory_all']);
export const worldcupResearchQuerySchema = z.object({
    season: z.coerce.number().pipe(importBatchScopeSeasonSchema).optional(),
    team: nonEmptyStringSchema.optional(),
    opponent: nonEmptyStringSchema.optional(),
    stage: nonEmptyStringSchema.optional(),
    groupRound: nonEmptyStringSchema.optional(),
    provider: researchProviderSchema.optional(),
    competitionCode: competitionCodeSchema.default('world_cup'),
    competitionCodes: competitionCodesQuerySchema,
    competitionName: nonEmptyStringSchema.optional(),
    fromYear: queryYearSchema,
    toYear: queryYearSchema,
    competitionTier: competitionTierSchema.optional(),
    researchView: worldcupResearchViewSchema.default('major_tournament'),
    minDataQualityScore: z.coerce.number().min(0).max(1).default(0.65),
    confidence: fieldConfidenceSchema.optional(),
    matchShape: worldcupResearchMatchShapeSchema.optional(),
    sort: worldcupResearchSortFieldSchema.optional(),
    sortDirection: sortDirectionSchema.default('asc'),
    includeComparisonRows: booleanQuerySchema.optional(),
    importBatchId: nonEmptyStringSchema.optional(),
    limit: limitSchema,
    offset: offsetSchema,
});
export const worldcupResearchAcceptanceTierSchema = z.enum(['canonical', 'context', 'rejected']);
export const worldcupResearchCoverageQuerySchema = z.object({
    season: z.coerce.number().pipe(importBatchScopeSeasonSchema).optional(),
    competitionCode: competitionCodeSchema.optional(),
    competitionCodes: competitionCodesQuerySchema,
    competitionName: nonEmptyStringSchema.optional(),
    fromYear: queryYearSchema,
    toYear: queryYearSchema,
    researchView: worldcupResearchViewSchema.default('major_tournament'),
    minDataQualityScore: z.coerce.number().min(0).max(1).default(0.65),
    importBatchId: nonEmptyStringSchema.optional(),
    coverageLimit: limitSchema,
    coverageOffset: offsetSchema,
    gapLimit: limitSchema,
    gapOffset: offsetSchema,
});
export const worldcupResearchFacetsQuerySchema = z.object({
    season: z.coerce.number().pipe(importBatchScopeSeasonSchema).optional(),
    competitionCode: competitionCodeSchema.optional(),
    competitionCodes: competitionCodesQuerySchema,
    competitionName: nonEmptyStringSchema.optional(),
    fromYear: queryYearSchema,
    toYear: queryYearSchema,
    researchView: worldcupResearchViewSchema.default('major_tournament'),
    minDataQualityScore: z.coerce.number().min(0).max(1).default(0.65),
    importBatchId: nonEmptyStringSchema.optional(),
});
export const providerValueEvidenceSchema = z.object({
    provider: researchProviderSchema,
    field: nonEmptyStringSchema,
    value: z.union([z.number(), z.string()]).nullable(),
    status: nonEmptyStringSchema.optional(),
});
export const worldcupResearchTeamMatchStatRowSchema = teamMatchStatSchema.extend({
    season: importBatchScopeSeasonSchema,
    competitionCode: competitionCodeSchema,
    competitionName: nonEmptyStringSchema,
    competitionTier: competitionTierSchema,
    dataQualityScore: qualityScoreSchema.nullable(),
    recommendedWeight: qualityScoreSchema.nullable(),
    matchHomeTeam: nonEmptyStringSchema.optional(),
    matchAwayTeam: nonEmptyStringSchema.optional(),
    matchKickoffAt: timestampSchema.nullable().optional(),
    homeGoals: nonNegativeIntSchema.optional(),
    awayGoals: nonNegativeIntSchema.optional(),
    opponentGoals: nonNegativeIntSchema,
    stage: nonEmptyStringSchema,
    groupRound: nonEmptyStringSchema.nullable(),
    providerEvidence: z.array(providerValueEvidenceSchema),
    comparisonOnly: z.boolean().optional(),
});
export const researchSourceSummarySchema = z.object({
    includedCompetitions: z.array(competitionCodeSchema),
    excludedCompetitions: z.array(competitionCodeSchema),
    qualityScoreRange: z.object({
        min: qualityScoreSchema.nullable(),
        max: qualityScoreSchema.nullable(),
    }),
    warnings: z.array(nonEmptyStringSchema),
});
export const worldcupResearchSourceUsageSchema = z.object({
    acceptanceTiers: z.array(worldcupResearchAcceptanceTierSchema),
    coverageVerdicts: z.array(nonEmptyStringSchema),
    coverageWarnings: z.array(nonEmptyStringSchema),
    competitionCodes: z.array(competitionCodeSchema),
    competitionNames: z.array(nonEmptyStringSchema),
    importBatchIds: z.array(nonEmptyStringSchema),
    rowCount: nonNegativeIntSchema,
});
export const worldcupResearchCoverageRowSchema = z.object({
    importBatchId: nonEmptyStringSchema,
    competitionCode: competitionCodeSchema,
    competitionName: nonEmptyStringSchema,
    season: importBatchScopeSeasonSchema,
    provider: nonEmptyStringSchema,
    fieldFamily: nonEmptyStringSchema,
    verdict: nonEmptyStringSchema,
    acceptanceTier: worldcupResearchAcceptanceTierSchema,
    dataQualityScore: qualityScoreSchema,
    recommendedWeight: qualityScoreSchema,
    fixtureCoverageRate: qualityScoreSchema,
    scoreCoverageRate: qualityScoreSchema,
    totalShotCoverageRate: qualityScoreSchema,
    shotsOnGoalCoverageRate: qualityScoreSchema,
    cornerCoverageRate: qualityScoreSchema,
    warnings: z.array(nonEmptyStringSchema),
    researchMatchCount: nonNegativeIntSchema,
    teamMatchStatRowCount: nonNegativeIntSchema,
});
export const worldcupResearchCoverageGapSchema = z.object({
    importBatchId: nonEmptyStringSchema,
    targetId: nonEmptyStringSchema.optional(),
    competitionCode: competitionCodeSchema.optional(),
    competitionName: nonEmptyStringSchema.optional(),
    season: importBatchScopeSeasonSchema.optional(),
    reason: nonEmptyStringSchema,
    acceptanceTier: z.literal('rejected'),
    warnings: z.array(nonEmptyStringSchema),
});
export const worldcupResearchCoverageResponseSchema = z.object({
    filters: worldcupResearchCoverageQuerySchema,
    activeImportBatchIds: z.array(nonEmptyStringSchema),
    summary: z.object({
        byAcceptanceTier: z.record(nonEmptyStringSchema, nonNegativeIntSchema),
        byVerdict: z.record(nonEmptyStringSchema, nonNegativeIntSchema),
        warningCounts: z.record(nonEmptyStringSchema, nonNegativeIntSchema),
        coverageRowCount: nonNegativeIntSchema,
        gapCount: nonNegativeIntSchema,
    }),
    pagination: z.object({
        coverageLimit: limitSchema,
        coverageOffset: offsetSchema,
        coverageTotal: nonNegativeIntSchema,
        coverageHasMore: z.boolean(),
        gapLimit: limitSchema,
        gapOffset: offsetSchema,
        gapTotal: nonNegativeIntSchema,
        gapHasMore: z.boolean(),
    }),
    rows: z.array(worldcupResearchCoverageRowSchema),
    gaps: z.array(worldcupResearchCoverageGapSchema),
    warnings: z.array(nonEmptyStringSchema),
});
export const worldcupResearchFacetItemSchema = z.object({
    value: nonEmptyStringSchema,
    label: nonEmptyStringSchema,
    teamMatchStatRowCount: nonNegativeIntSchema,
    researchMatchCount: nonNegativeIntSchema,
});
export const worldcupResearchFacetsResponseSchema = z.object({
    appliedFilters: worldcupResearchFacetsQuerySchema,
    activeImportBatchIds: z.array(nonEmptyStringSchema),
    facets: z.object({
        seasons: z.array(worldcupResearchFacetItemSchema),
        competitionCodes: z.array(worldcupResearchFacetItemSchema),
        competitionNames: z.array(worldcupResearchFacetItemSchema),
        teams: z.array(worldcupResearchFacetItemSchema),
        opponents: z.array(worldcupResearchFacetItemSchema),
        providers: z.array(worldcupResearchFacetItemSchema),
        importBatchIds: z.array(worldcupResearchFacetItemSchema),
    }),
    warnings: z.array(nonEmptyStringSchema),
});
export const worldcupResearchTeamMatchStatsResponseSchema = z.object({
    appliedFilters: worldcupResearchQuerySchema,
    totalRows: nonNegativeIntSchema,
    limit: limitSchema,
    offset: offsetSchema,
    sourceSummary: researchSourceSummarySchema,
    rows: z.array(worldcupResearchTeamMatchStatRowSchema),
});
export const publicResearchTeamIdentitySchema = z.object({
    slug: nonEmptyStringSchema,
    name: nonEmptyStringSchema,
    aliases: z.array(nonEmptyStringSchema),
});
export const publicResearchQualitySummarySchema = z.object({
    dataTiers: z.array(nonEmptyStringSchema),
    warnings: z.array(nonEmptyStringSchema),
    fieldConfidence: fieldConfidenceMapSchema,
    sourceUsage: z.object({
        rowCount: nonNegativeIntSchema,
        importBatchIds: z.array(nonEmptyStringSchema),
        competitionCodes: z.array(competitionCodeSchema),
    }),
});
export const publicResearchProfileSummarySchema = z.object({
    matches: nonNegativeIntSchema,
    teamStatRows: nonNegativeIntSchema,
    wins: nonNegativeIntSchema.nullable(),
    draws: nonNegativeIntSchema.nullable(),
    losses: nonNegativeIntSchema.nullable(),
    avgGoalsFor: z.number().nullable(),
    avgGoalsAgainst: z.number().nullable(),
    avgShotsTotal: z.number().nullable(),
    avgShotsOnTarget: z.number().nullable(),
    avgCorners: z.number().nullable(),
    avgExpectedGoals: z.number().nullable(),
});
export const publicResearchTrendPointSchema = z.object({
    matchId: nonEmptyStringSchema,
    kickoffAt: timestampSchema.nullable(),
    season: importBatchScopeSeasonSchema,
    opponent: nonEmptyStringSchema,
    goals: nonNegativeIntSchema,
    shotsTotal: nullableNonNegativeIntSchema,
    shotsOnTarget: nullableNonNegativeIntSchema,
    corners: nullableNonNegativeIntSchema,
    expectedGoals: z.number().min(0).nullable(),
});
export const publicResearchHeadToHeadMatchSchema = z.object({
    matchId: nonEmptyStringSchema,
    season: importBatchScopeSeasonSchema,
    competitionName: nonEmptyStringSchema.nullable(),
    stage: nonEmptyStringSchema.nullable(),
    home: worldcupResearchTeamMatchStatRowSchema.nullable(),
    away: worldcupResearchTeamMatchStatRowSchema.nullable(),
});
export const publicResearchCommonOpponentMatchSchema = z.object({
    matchId: nonEmptyStringSchema,
    season: importBatchScopeSeasonSchema,
    competitionName: nonEmptyStringSchema.nullable(),
    stage: nonEmptyStringSchema.nullable(),
    row: worldcupResearchTeamMatchStatRowSchema,
});
export const publicResearchCommonOpponentGroupSchema = z.object({
    opponent: nonEmptyStringSchema,
    home: z.array(publicResearchCommonOpponentMatchSchema),
    away: z.array(publicResearchCommonOpponentMatchSchema),
});
export const publicResearchHalfTimeMetricGroupSchema = z.enum([
    'attack_pressure',
    'possession_passing',
    'set_piece_territory',
    'defensive_goalkeeping',
    'discipline',
]);
export const publicResearchHalfTimeMetricSchema = z.object({
    key: nonEmptyStringSchema,
    labelEn: nonEmptyStringSchema,
    labelZh: nonEmptyStringSchema,
    group: publicResearchHalfTimeMetricGroupSchema,
    firstHalfValue: z.number().nullable(),
    secondHalfValue: z.number().nullable(),
    delta: z.number().nullable(),
    homeFirstHalfValue: z.number().nullable().optional(),
    homeSecondHalfValue: z.number().nullable().optional(),
    awayFirstHalfValue: z.number().nullable().optional(),
    awaySecondHalfValue: z.number().nullable().optional(),
    unit: z.enum(['count', 'percent']),
    role: z.enum(['label_driver', 'supporting_context']),
    coverageStatus: z.enum(['available', 'partial', 'missing']),
});
export const publicResearchHalfTimeRecentExampleSchema = z.object({
    matchId: nonEmptyStringSchema,
    kickoffAt: timestampSchema.nullable(),
    opponent: nonEmptyStringSchema,
    labelKey: z.enum([
        'balanced',
        'first_half_stronger',
        'second_half_stronger',
        'insufficient_required_metrics',
    ]),
    firstHalfScore: z.number(),
    secondHalfScore: z.number(),
});
export const publicResearchMatchHalfTimeRhythmSchema = z.object({
    status: z.enum(['available', 'partial', 'unavailable']),
    requiredMetricCoverage: z.object({
        required: z.array(nonEmptyStringSchema),
        available: z.array(nonEmptyStringSchema),
        missing: z.array(nonEmptyStringSchema),
    }),
    metrics: z.array(publicResearchHalfTimeMetricSchema),
});
export const publicResearchTeamHalfTimeRhythmSchema = z.object({
    status: z.enum(['available', 'insufficient_sample', 'unavailable']),
    labelKey: z.enum([
        'balanced',
        'starts_fast',
        'improves_after_half_time',
        'insufficient_sample',
        'missing_half_time_data',
    ]),
    sample: z.object({
        usableMatches: nonNegativeIntSchema,
        totalMatchesConsidered: nonNegativeIntSchema,
        minimumRequired: z.literal(5),
        maximumIncluded: z.literal(50),
    }),
    requiredMetricCoverage: z.object({
        required: z.array(nonEmptyStringSchema),
        available: z.array(nonEmptyStringSchema),
        missing: z.array(nonEmptyStringSchema),
    }),
    averages: z.array(publicResearchHalfTimeMetricSchema),
    recentExamples: z.array(publicResearchHalfTimeRecentExampleSchema),
});
export const publicResearchTeamProfileQuerySchema = worldcupResearchQuerySchema.omit({
    team: true,
}).partial();
export const publicResearchHeadToHeadQuerySchema = worldcupResearchQuerySchema.extend({
    team: nonEmptyStringSchema,
    opponent: nonEmptyStringSchema,
});
export const publicResearchTeamProfileResponseSchema = z.object({
    team: publicResearchTeamIdentitySchema,
    appliedFilters: worldcupResearchQuerySchema,
    summary: publicResearchProfileSummarySchema,
    quality: publicResearchQualitySummarySchema,
    trend: z.array(publicResearchTrendPointSchema),
    rows: z.array(worldcupResearchTeamMatchStatRowSchema),
    halfTimeRhythm: publicResearchTeamHalfTimeRhythmSchema.optional(),
    allowedUse: z.array(nonEmptyStringSchema),
    prohibitedUse: z.array(nonEmptyStringSchema),
});
export const publicResearchHeadToHeadResponseSchema = z.object({
    teams: z.object({
        home: publicResearchTeamIdentitySchema,
        away: publicResearchTeamIdentitySchema,
    }),
    appliedFilters: publicResearchHeadToHeadQuerySchema,
    summary: z.object({
        home: publicResearchProfileSummarySchema,
        away: publicResearchProfileSummarySchema,
    }),
    quality: publicResearchQualitySummarySchema,
    matches: z.array(publicResearchHeadToHeadMatchSchema),
    commonOpponents: z.array(publicResearchCommonOpponentGroupSchema),
    allowedUse: z.array(nonEmptyStringSchema),
    prohibitedUse: z.array(nonEmptyStringSchema),
});
export const publicResearchMatchDetailStorageStatusSchema = z.enum([
    'canonical',
    'provider_normalized',
    'raw_payload_only',
    'requires_new_fetch',
]);
const matchDetailMetricValueSchema = z.union([z.number(), z.string()]).nullable();
export const publicResearchMatchDetailMetricSchema = z.object({
    key: nonEmptyStringSchema,
    labelEn: nonEmptyStringSchema,
    labelZh: nonEmptyStringSchema,
    homeValue: matchDetailMetricValueSchema,
    awayValue: matchDetailMetricValueSchema,
    sourceProvider: researchProviderSchema,
    sourceEndpoint: nonEmptyStringSchema,
    confidence: fieldConfidenceSchema,
    storageStatus: publicResearchMatchDetailStorageStatusSchema,
    definitionNote: nonEmptyStringSchema,
    rawPayloadId: nonEmptyStringSchema.optional(),
    payloadHash: nonEmptyStringSchema.optional(),
    providerMatchId: nonEmptyStringSchema.optional(),
});
export const publicResearchMatchDetailGroupsSchema = z.object({
    attacking: z.array(publicResearchMatchDetailMetricSchema),
    possession: z.array(publicResearchMatchDetailMetricSchema),
    discipline: z.array(publicResearchMatchDetailMetricSchema),
    goalkeeping: z.array(publicResearchMatchDetailMetricSchema),
    lineups: z.array(publicResearchMatchDetailMetricSchema),
});
export const publicResearchMatchDetailCoverageSchema = z.object({
    availableNow: z.array(nonEmptyStringSchema),
    rawOnly: z.array(nonEmptyStringSchema),
    requiresNewFetch: z.array(nonEmptyStringSchema),
});
export const publicResearchMatchDetailCoreSideSchema = z.object({
    goals: nonNegativeIntSchema,
    shotsTotal: nullableNonNegativeIntSchema,
    shotsOnTarget: nullableNonNegativeIntSchema,
    corners: nullableNonNegativeIntSchema,
    expectedGoals: z.number().min(0).nullable(),
    fieldConfidence: fieldConfidenceMapSchema,
    fieldConfidenceByProvider: fieldConfidenceByProviderSchema,
    sourceReferences: z.array(evidenceSourceReferenceSchema),
});
export const publicResearchMatchDetailLineupPlayerSchema = z.object({
    providerPlayerId: nonNegativeIntSchema.nullable(),
    name: nonEmptyStringSchema,
    number: nonNegativeIntSchema.nullable(),
    position: nonEmptyStringSchema.nullable(),
    grid: nonEmptyStringSchema.nullable(),
});
export const publicResearchMatchDetailCoachSchema = z.object({
    providerCoachId: nonNegativeIntSchema.nullable(),
    name: nonEmptyStringSchema,
    photo: nonEmptyStringSchema.optional(),
});
export const publicResearchMatchDetailPlayerStatSchema = z.object({
    providerPlayerId: nonNegativeIntSchema.nullable(),
    name: nonEmptyStringSchema,
    number: nonNegativeIntSchema.nullable(),
    position: nonEmptyStringSchema.nullable(),
    minutes: nullableNonNegativeIntSchema,
    rating: z.number().min(0).nullable(),
    isCaptain: z.boolean().nullable(),
    isSubstitute: z.boolean().nullable(),
    goals: nullableNonNegativeIntSchema,
    assists: nullableNonNegativeIntSchema,
    saves: nullableNonNegativeIntSchema,
    conceded: nullableNonNegativeIntSchema,
    shotsTotal: nullableNonNegativeIntSchema,
    shotsOnTarget: nullableNonNegativeIntSchema,
    passesTotal: nullableNonNegativeIntSchema,
    passesKey: nullableNonNegativeIntSchema,
    passesAccuracy: nullableNonNegativeIntSchema,
    tacklesTotal: nullableNonNegativeIntSchema,
    tacklesBlocks: nullableNonNegativeIntSchema,
    interceptions: nullableNonNegativeIntSchema,
    duelsTotal: nullableNonNegativeIntSchema,
    duelsWon: nullableNonNegativeIntSchema,
    dribblesAttempts: nullableNonNegativeIntSchema,
    dribblesSuccess: nullableNonNegativeIntSchema,
    foulsDrawn: nullableNonNegativeIntSchema,
    foulsCommitted: nullableNonNegativeIntSchema,
    yellowCards: nullableNonNegativeIntSchema,
    redCards: nullableNonNegativeIntSchema,
});
export const publicResearchMatchDetailRosterSideSchema = z.object({
    teamName: nonEmptyStringSchema,
    formation: nonEmptyStringSchema.nullable(),
    coach: publicResearchMatchDetailCoachSchema.nullable(),
    startingXi: z.array(publicResearchMatchDetailLineupPlayerSchema),
    substitutes: z.array(publicResearchMatchDetailLineupPlayerSchema),
    playerStats: z.array(publicResearchMatchDetailPlayerStatSchema),
});
export const publicResearchMatchDetailRosterDetailSchema = z.object({
    sourceProvider: z.literal('api_football'),
    sourceEndpoints: z.array(z.enum(['/fixtures/lineups', '/fixtures/players'])),
    home: publicResearchMatchDetailRosterSideSchema,
    away: publicResearchMatchDetailRosterSideSchema,
});
export const publicResearchMatchTimelineEventSchema = z.object({
    minute: z.number(),
    extraMinute: z.number().nullable(),
    type: z.enum(['goal', 'card', 'subst', 'var']),
    detail: z.string(),
    teamName: z.string(),
    playerName: z.string().nullable(),
    assistName: z.string().nullable(),
});
export const publicResearchMatchMarketSignalsSchema = z.object({
    status: z.enum(['available', 'unavailable']),
    preMatchImpliedProbability: z.object({
        home: z.number().nullable(),
        draw: z.number().nullable(),
        away: z.number().nullable(),
    }).optional(),
    finalOdds: z.object({
        home: z.number().nullable(),
        draw: z.number().nullable(),
        away: z.number().nullable(),
        source: z.string().optional(),
    }).optional(),
    liquidityEstimate: z.number().nullable().optional(),
});
export const publicResearchMatchDetailResponseSchema = z.object({
    match: z.object({
        matchId: nonEmptyStringSchema,
        importBatchId: nonEmptyStringSchema,
        season: importBatchScopeSeasonSchema,
        competitionCode: competitionCodeSchema,
        competitionName: nonEmptyStringSchema,
        competitionTier: competitionTierSchema,
        dataQualityScore: qualityScoreSchema.nullable(),
        recommendedWeight: qualityScoreSchema.nullable(),
        stage: nonEmptyStringSchema,
        groupRound: nonEmptyStringSchema.nullable(),
        kickoffAt: timestampSchema.nullable(),
        homeTeam: nonEmptyStringSchema,
        awayTeam: nonEmptyStringSchema,
        score: z.object({
            home: nonNegativeIntSchema.nullable(),
            away: nonNegativeIntSchema.nullable(),
        }),
    }),
    canonicalCore: z.object({
        home: publicResearchMatchDetailCoreSideSchema.nullable(),
        away: publicResearchMatchDetailCoreSideSchema.nullable(),
    }),
    analysisEnrichment: publicResearchMatchDetailGroupsSchema,
    halfTimeRhythm: publicResearchMatchHalfTimeRhythmSchema.optional(),
    timeline: z.array(publicResearchMatchTimelineEventSchema).optional(),
    marketSignals: publicResearchMatchMarketSignalsSchema.optional(),
    rosterDetail: publicResearchMatchDetailRosterDetailSchema.optional(),
    sourceNotes: z.array(nonEmptyStringSchema),
    coverage: publicResearchMatchDetailCoverageSchema,
    allowedUse: z.array(nonEmptyStringSchema),
    prohibitedUse: z.array(nonEmptyStringSchema),
});
export const worldcupResearchTeamPerformanceRowSchema = z.object({
    season: importBatchScopeSeasonSchema,
    team: nonEmptyStringSchema,
    matches: nonNegativeIntSchema,
    goals: nonNegativeIntSchema,
    opponentGoals: nonNegativeIntSchema,
    shotsTotal: nonNegativeIntSchema,
    shotsOnTarget: nonNegativeIntSchema,
    corners: nonNegativeIntSchema,
    expectedGoals: z.number().min(0),
});
export const worldcupResearchStagePerformanceRowSchema = z.object({
    season: importBatchScopeSeasonSchema,
    stage: nonEmptyStringSchema,
    teams: nonNegativeIntSchema,
    matches: nonNegativeIntSchema,
    goals: nonNegativeIntSchema,
    shotsTotal: nonNegativeIntSchema,
    shotsOnTarget: nonNegativeIntSchema,
    corners: nonNegativeIntSchema,
    expectedGoals: z.number().min(0),
});
export const worldcupResearchMatchShapeRowSchema = z.object({
    shape: worldcupResearchMatchShapeSchema,
    rows: z.array(worldcupResearchTeamMatchStatRowSchema),
});
export const worldcupResearchViewResponseSchema = z.object({
    appliedFilters: worldcupResearchQuerySchema,
    rows: z.array(z.unknown()),
});
export const worldcupResearchTeamPerformanceResponseSchema = worldcupResearchViewResponseSchema.extend({
    rows: z.array(worldcupResearchTeamPerformanceRowSchema),
});
export const worldcupResearchStagePerformanceResponseSchema = worldcupResearchViewResponseSchema.extend({
    rows: z.array(worldcupResearchStagePerformanceRowSchema),
});
export const worldcupResearchMatchShapesResponseSchema = worldcupResearchViewResponseSchema.extend({
    rows: z.array(worldcupResearchMatchShapeRowSchema),
});
export const worldcupResearchExportFormatSchema = z.enum(['json', 'csv']);
export const worldcupResearchExportQuerySchema = worldcupResearchQuerySchema.extend({
    format: worldcupResearchExportFormatSchema.default('json'),
});
export const worldcupResearchExportResponseSchema = z.object({
    format: worldcupResearchExportFormatSchema,
    contentType: nonEmptyStringSchema,
    filename: nonEmptyStringSchema,
    appliedFilters: worldcupResearchExportQuerySchema,
    sourceSummary: researchSourceSummarySchema,
    sourceBatches: z.array(nonEmptyStringSchema),
    rows: z.array(worldcupResearchTeamMatchStatRowSchema),
    csv: z.string().optional(),
});
export const worldcupResearchProviderAuditRowSchema = z.object({
    matchId: nonEmptyStringSchema,
    importBatchId: nonEmptyStringSchema,
    season: importBatchScopeSeasonSchema,
    team: nonEmptyStringSchema,
    field: nonEmptyStringSchema,
    statsbombValue: z.union([z.number(), z.string()]).nullable(),
    apiFootballValue: z.union([z.number(), z.string()]).nullable(),
    status: nonEmptyStringSchema,
    magnitude: z.number().nullable(),
    fieldConfidence: fieldConfidenceSchema,
    fieldConfidenceByProvider: fieldConfidenceByProviderSchema,
});
export const worldcupResearchProviderAuditResponseSchema = z.object({
    appliedFilters: worldcupResearchQuerySchema,
    rows: z.array(worldcupResearchProviderAuditRowSchema),
});
export const worldcupResearchImportBatchSummarySchema = importBatchSchema.extend({
    payloadCount: nonNegativeIntSchema,
    fetchedCount: nonNegativeIntSchema,
    reusedCount: nonNegativeIntSchema,
    unresolvedRows: nonNegativeIntSchema,
    unresolvedRowDetails: z.array(adminSafeImportIssueRowSchema),
    failedRows: z.array(adminSafeImportIssueRowSchema),
    providerAuditSummary: adminSafeProviderAuditSummarySchema,
    active: z.boolean(),
});
export const worldcupResearchImportBatchesResponseSchema = z.object({
    rows: z.array(worldcupResearchImportBatchSummarySchema),
});
export const completedWorldcupResearchImportModeSchema = z.enum(['dry_run', 'execute', 'retry', 'status']);
export const completedWorldcupResearchImportSelectorSchema = z.object({
    fixtureIds: z.array(z.coerce.number().int().positive()).default([]),
    team: nonEmptyStringSchema.optional(),
    competitionCode: competitionCodeSchema.default('world_cup'),
    season: z.coerce.number().pipe(importBatchScopeSeasonSchema).default(2026),
    fromKickoffAt: timestampSchema.optional(),
    toKickoffAt: timestampSchema.optional(),
    fixtureLimit: z.coerce.number().int().min(1).max(100).optional(),
});
export const completedWorldcupResearchImportRequestSchema = z.object({
    mode: completedWorldcupResearchImportModeSchema,
    selector: completedWorldcupResearchImportSelectorSchema,
    execute: z.boolean().default(false),
    maxProviderRequests: z.coerce.number().int().min(0).max(100).default(0),
    settleWindowMinutes: z.coerce.number().int().min(0).max(4320).default(120),
});
export const completedWorldcupResearchImportEndpointSchema = z.enum([
    '/fixtures',
    '/fixtures/statistics',
    '/fixtures/events',
    '/fixtures/lineups',
    '/fixtures/players',
]);
export const completedWorldcupResearchImportVerdictSchema = z.enum([
    'deferred',
    'dry_run',
    'accepted',
    'partial',
    'failed',
    'budget_deferred',
    'already_accepted',
]);
export const completedWorldcupResearchImportFixtureEvidenceSchema = z.object({
    fixtureId: z.number().int().positive(),
    providerFixtureId: z.number().int().positive(),
    importBatchId: nonEmptyStringSchema.nullable(),
    verdict: completedWorldcupResearchImportVerdictSchema,
    plannedEndpoints: z.array(completedWorldcupResearchImportEndpointSchema),
    reusedPayloadCount: nonNegativeIntSchema,
    providerRequestCount: nonNegativeIntSchema,
    acceptedRowCount: nonNegativeIntSchema,
    missingCoverage: z.array(nonEmptyStringSchema),
    smokePath: nonEmptyStringSchema.nullable(),
    messages: z.array(nonEmptyStringSchema),
});
export const completedWorldcupResearchImportResponseSchema = z.object({
    runId: nonEmptyStringSchema,
    mode: completedWorldcupResearchImportModeSchema,
    dryRun: z.boolean(),
    generatedAt: timestampSchema,
    selector: completedWorldcupResearchImportSelectorSchema,
    summary: z.object({
        fixtureCount: nonNegativeIntSchema,
        acceptedFixtureCount: nonNegativeIntSchema,
        partialFixtureCount: nonNegativeIntSchema,
        failedFixtureCount: nonNegativeIntSchema,
        deferredFixtureCount: nonNegativeIntSchema,
        providerRequestCount: nonNegativeIntSchema,
        reusedPayloadCount: nonNegativeIntSchema,
    }),
    fixtures: z.array(completedWorldcupResearchImportFixtureEvidenceSchema),
});
export const worldcupResearchEvidencePackageSchema = z.object({
    allowedUse: z.array(nonEmptyStringSchema),
    prohibitedUse: z.array(nonEmptyStringSchema),
    appliedFilters: z.record(nonEmptyStringSchema, z.unknown()),
    sourceSummary: researchSourceSummarySchema,
    rows: z.array(z.unknown()).optional(),
    summary: z.unknown().optional(),
    fieldConfidence: z.array(z.record(nonEmptyStringSchema, z.unknown())).optional(),
    providerEvidence: z.array(z.unknown()).optional(),
    warnings: z.array(nonEmptyStringSchema),
    sourceBatches: z.array(nonEmptyStringSchema),
    sourceUsage: worldcupResearchSourceUsageSchema,
});
const researchSourceShapingRequestSchema = z.object({
    competitionCode: competitionCodeSchema.default('world_cup'),
    competitionName: nonEmptyStringSchema.optional(),
    researchView: worldcupResearchViewSchema.default('major_tournament'),
    minDataQualityScore: z.coerce.number().min(0).max(1).default(0.65),
});
export const summarizeTeamRequestSchema = researchSourceShapingRequestSchema.extend({
    season: z.coerce.number().pipe(importBatchScopeSeasonSchema).optional(),
    team: nonEmptyStringSchema,
    provider: researchProviderSchema.optional(),
    importBatchId: nonEmptyStringSchema.optional(),
});
export const compareTeamsRequestSchema = researchSourceShapingRequestSchema.extend({
    season: z.coerce.number().pipe(importBatchScopeSeasonSchema).optional(),
    teams: z.tuple([nonEmptyStringSchema, nonEmptyStringSchema]),
    provider: researchProviderSchema.optional(),
    importBatchId: nonEmptyStringSchema.optional(),
});
export const researchWorkflowIntentSchema = z.enum(['team_summary', 'team_comparison', 'team_match_search']);
export const researchWorkflowAnalysisToolSchema = z.enum(['summarize_team', 'compare_teams', 'search_team_match_stats']);
export const runResearchWorkflowRequestSchema = z.object({
    intent: researchWorkflowIntentSchema,
    scope: worldcupResearchFacetsQuerySchema,
    team: nonEmptyStringSchema.optional(),
    teams: z.tuple([nonEmptyStringSchema, nonEmptyStringSchema]).optional(),
    opponent: nonEmptyStringSchema.optional(),
    provider: researchProviderSchema.optional(),
    limit: limitSchema,
    offset: offsetSchema,
    sort: worldcupResearchSortFieldSchema.optional(),
    sortDirection: sortDirectionSchema.default('asc'),
}).superRefine((input, ctx) => {
    if (input.intent === 'team_summary' && input.team === undefined) {
        ctx.addIssue({
            code: 'custom',
            path: ['team'],
            message: 'team is required for team_summary',
        });
    }
    if (input.intent === 'team_comparison' && input.teams === undefined) {
        ctx.addIssue({
            code: 'custom',
            path: ['teams'],
            message: 'teams is required for team_comparison',
        });
    }
});
export const researchWorkflowFacetSelectionSchema = z.object({
    competitionName: nonEmptyStringSchema.optional(),
    team: nonEmptyStringSchema.optional(),
    teams: z.array(nonEmptyStringSchema).optional(),
    opponent: nonEmptyStringSchema.optional(),
    provider: researchProviderSchema.optional(),
});
export const runResearchWorkflowResponseSchema = z.object({
    intent: researchWorkflowIntentSchema,
    status: z.enum(['completed', 'skipped']),
    reason: nonEmptyStringSchema.optional(),
    discovery: worldcupResearchFacetsResponseSchema,
    selected: researchWorkflowFacetSelectionSchema,
    analysisTool: researchWorkflowAnalysisToolSchema.nullable(),
    analysisFilters: z.record(nonEmptyStringSchema, z.unknown()).nullable(),
    evidencePackage: worldcupResearchEvidencePackageSchema.optional(),
    warnings: z.array(nonEmptyStringSchema),
});
